import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { getAvailability, scheduleDemo } from "../services/bookingsService";
import { fetchAgents, type Agent } from "../services/agentsService";
import type { Lead } from "../services/leadsService";

// ===========================================================================
// Agendar demo — schedules a DEMO from a landing lead. Picks the agent, the
// date/time and the prospect's email, then calls POST /crm/bookings/demo which
// creates the booking, the Google Calendar event (dual calendar + Meet link)
// and emails the themed invitation (with an .ics fallback) to both parties.
// ===========================================================================

const DURATIONS = [15, 30, 45, 60, 90];

export default function ScheduleDemoModal({
  lead,
  isDarkMode,
  onClose,
  onScheduled,
}: {
  lead: Lead;
  isDarkMode: boolean;
  onClose: () => void;
  onScheduled?: () => void;
}) {
  const tc = buildThemeClasses();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentId, setAgentId] = useState("");
  const [tz, setTz] = useState("America/La_Paz");
  const [offset, setOffset] = useState("-04:00");

  // Defaults: tomorrow at 10:00, 30 min.
  const tomorrowKey = useMemo(() => {
    const d = new Date(Date.now() + 86400000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const [date, setDate] = useState(tomorrowKey);
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(30);
  const [title, setTitle] = useState(`Demo — ${lead.name || lead.company || "Prospecto"}`);
  const [email, setEmail] = useState(lead.email || "");
  const [notes, setNotes] = useState(lead.problem || "");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [page, av] = await Promise.all([
          fetchAgents({ type: "human", limit: 300 }),
          getAvailability(),
        ]);
        if (!alive) return;
        setAgents(page.data);
        setAgentId((prev) => prev || page.data[0]?.id || "");
        if (av) {
          setTz(av.timezone || "America/La_Paz");
          setOffset(av.utcOffset || "-04:00");
        }
      } catch {
        /* keep defaults */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const selectedAgent = useMemo(() => agents.find((a) => a.id === agentId) || null, [agents, agentId]);
  const noAgents = !loading && agents.length === 0;

  const submit = async () => {
    setErr(null);
    if (!agentId) {
      setErr("Selecciona un agente.");
      return;
    }
    if (!email.trim()) {
      setErr("Captura el correo del prospecto para enviar la invitación.");
      return;
    }
    setBusy(true);
    try {
      const start = `${date}T${time}:00${offset}`;
      const booking = await scheduleDemo({
        leadId: lead.id,
        agentId,
        start,
        durationMinutes: duration,
        title: title.trim() || undefined,
        notes: notes.trim() || undefined,
        email: email.trim(),
        timezone: tz,
      });
      const meet = booking?.meetingUrl;
      await Swal.fire({
        icon: "success",
        title: "Demo agendada",
        html: `Se creó la demo y se ${booking?.emailSent ? "envió" : "intentó enviar"} la invitación a <b>${email.trim()}</b>.${
          meet ? `<br/><br/><a href="${meet}" target="_blank" rel="noreferrer" style="color:${brand.primary}">Abrir enlace de Google Meet</a>` : ""
        }`,
        confirmButtonColor: brand.primary,
      });
      onScheduled?.();
      onClose();
    } catch (e) {
      const error = e as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string };
      setErr(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "No se pudo agendar la demo",
      );
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Agendar demo"
      themeClasses={tc}
      onClose={onClose}
      width="max-w-lg"
      content={
        <div className="space-y-3">
          <p className={`text-sm ${tc.textSecondary}`}>
            Agenda una demostración para <b className={tc.text}>{lead.name || lead.company || "este lead"}</b>. La cita
            se crea en el calendario del agente y se le envía la invitación al prospecto (con enlace de Meet y archivo .ics).
          </p>

          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</p>}

          {noAgents ? (
            <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
              No hay agentes humanos. Crea uno en Configuración → Agentes → Humanos.
            </p>
          ) : (
            <>
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Agente</label>
                <select
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  disabled={loading}
                  className={`w-full rounded-lg border px-3 py-2 text-sm outline-none ${
                    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
                  }`}
                >
                  {loading ? (
                    <option>Cargando…</option>
                  ) : (
                    agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                        {a.role ? ` · ${a.role}` : ""}
                        {a.googleCalendar?.connected ? " (Google ✓)" : ""}
                      </option>
                    ))
                  )}
                </select>
                {selectedAgent && !selectedAgent.googleCalendar?.connected && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    <i className="ri-error-warning-line mr-1" />
                    Este agente no ha conectado su Google Calendar; la cita usará el calendario global si está configurado.
                  </p>
                )}
              </div>

              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Título</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Fecha</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Hora</label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Duración</label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className={`w-full rounded-lg border px-2 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>
                        {d} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className={`text-[11px] ${tc.textSecondary}`}>Zona horaria: {tz}</p>

              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Correo del prospecto</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@empresa.com"
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                />
                {!lead.email && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    El lead no traía correo. Captúralo para poder invitarlo.
                  </p>
                )}
              </div>

              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Notas</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={busy || loading || noAgents}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-60"
            >
              {busy ? "Agendando…" : "Agendar demo"}
            </button>
          </div>
        </div>
      }
    />
  );
}
